import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BlogFeaturedImage } from '@/components/blog/BlogFeaturedImage';
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import { Loader2, Calendar, Clock, Tag, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import LandingNavbar from "./components/LandingNavbar";
import LandingFooter from "./components/LandingFooter";
import {
  computeNavbarFeatureFlags,
  getCleanedServices,
} from "./landingPageSectionsConfig";
import "./landing-theme.css";
import { sanitizeBlogHtml } from '@/lib/blog/content-html';

interface Blog {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  featured_image_url: string | null;
  status: string;
  published_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  author_name: string | null;
  read_time_minutes: number | null;
  views_count: number;
  tags: string[] | null;
  category: string | null;
}

interface SubdomainData {
  subdomain: string;
  theme_config: any;
  location: {
    id: string;
    name: string;
  };
  workspace: {
    id: string;
    name: string;
    brand_name: string;
    logo_url: string;
  };
}

interface NavigationFlags {
  hasPublishedPosts: boolean;
  hasReviews: boolean;
  hasBlogs: boolean;
  hasProductsServices: boolean;
  hasGallery: boolean;
  hasSocial: boolean;
}

interface BlogDetailPageProps {
  ssrBlog?: Blog;
  ssrSubdomainData?: SubdomainData;
  ssrNavigationFlags?: NavigationFlags;
  subdomain?: string;
  slug?: string;
}

const BlogDetailPage = ({
  ssrBlog,
  ssrSubdomainData,
  ssrNavigationFlags,
  subdomain: subdomainProp,
  slug: slugProp
}: BlogDetailPageProps = {}) => {
  const params = useParams<{ subdomain?: string; slug?: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(ssrBlog || null);
  const [subdomainData, setSubdomainData] = useState<SubdomainData | null>(ssrSubdomainData || null);
  const [loading, setLoading] = useState(!ssrBlog);
  const [error, setError] = useState<string | null>(null);
  const [hasPublishedPosts, setHasPublishedPosts] = useState(ssrNavigationFlags?.hasPublishedPosts || false);
  const [hasReviews, setHasReviews] = useState(ssrNavigationFlags?.hasReviews || false);
  const [hasBlogs, setHasBlogs] = useState(ssrNavigationFlags?.hasBlogs || false);
  const [hasProductsServices, setHasProductsServices] = useState(ssrNavigationFlags?.hasProductsServices || false);
  const [hasGallery, setHasGallery] = useState(ssrNavigationFlags?.hasGallery || false);
  const [hasSocial, setHasSocial] = useState(ssrNavigationFlags?.hasSocial || false);
  const [hasServices, setHasServices] = useState(false);
  const [hasAppointment, setHasAppointment] = useState(false);
  const [location, setLocation] = useState<any>(null);
  const [workspace, setWorkspace] = useState<any>(null);

  // Extract subdomain from URL params OR hostname
  const getSubdomain = () => {
    // First check if passed as prop (from SSR)
    if (subdomainProp) {
      return subdomainProp;
    }

    // Then check URL params (for /subdomain/:subdomain route)
    if (params.subdomain) {
      return params.subdomain;
    }

    // Finally check hostname (for subdomain.localhost or subdomain.my-domain.com)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // For subdomain.localhost or subdomain.my-domain.com
      if (parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        return parts[0];
      }
    }

    return null;
  };

  const subdomain = getSubdomain();
  const slug = slugProp || params.slug;

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  useEffect(() => {
    // If we have SSR data, don't fetch again
    if (ssrBlog && ssrSubdomainData) {
      console.log("[BlogDetailPage] Using SSR data, skipping client-side fetch");
      return;
    }

    // Otherwise, fetch data client-side
    if (subdomain && slug) {
      fetchBlogAndSubdomainData();
    }
  }, [subdomain, slug, ssrBlog, ssrSubdomainData]);

  // Compute navbar feature flags when location/workspace/subdomainData changes
  useEffect(() => {
    if (location && workspace && subdomainData) {
      const availabilityData = {
        location,
        workspace,
        hasReviews,
        hasPublishedPosts,
        hasBlogs,
        hasProductsServices,
        customizations: subdomainData.theme_config?.customizations || {},
        getCleanedServices,
      };

      const featureFlags = computeNavbarFeatureFlags(availabilityData);
      setHasServices(featureFlags.hasServices);
      setHasGallery(featureFlags.hasGallery);
      setHasSocial(featureFlags.hasSocial);
      setHasAppointment(featureFlags.hasAppointment);
    }
  }, [location, workspace, subdomainData, hasReviews, hasPublishedPosts, hasBlogs, hasProductsServices]);

  const fetchBlogAndSubdomainData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching blog with subdomain:", subdomain, "and slug:", slug);

      // Fetch subdomain data
      const { data: subdomainConfig, error: subdomainError } = await supabase
        .from("subdomain_configs")
        .select("*")
        .eq("subdomain", subdomain)
        .eq("subdomain_type", "landing")
        .eq("is_active", true)
        .single();

      if (subdomainError || !subdomainConfig) {
        console.error("Subdomain error:", subdomainError);
        throw new Error("Subdomain not found");
      }

      // Fetch location data (with all fields needed for navbar)
      const { data: locationData, error: locationError } = await supabase
        .from("gmb_locations")
        .select("id, name, primary_phone, auto_detected_services, photos")
        .eq("id", subdomainConfig.location_id)
        .single();

      if (locationError) {
        console.error("Location error:", locationError);
      }

      // Fetch workspace data (with all fields needed for navbar)
      const { data: workspaceData, error: workspaceError } = await supabase
        .from("workspaces")
        .select("id, name, brand_name, logo_url, facebook_url, instagram_url, twitter_url, linkedin_url, youtube_url")
        .eq("id", subdomainConfig.workspace_id)
        .single();

      if (workspaceError) {
        console.error("Workspace error:", workspaceError);
      }

      // Store location and workspace data for navbar
      setLocation(locationData);
      setWorkspace(workspaceData);

      setSubdomainData({
        subdomain: subdomainConfig.subdomain,
        theme_config: subdomainConfig.theme_config,
        location: locationData || { id: "", name: "" },
        workspace: workspaceData || { id: "", name: "", brand_name: "", logo_url: "" },
      });

      // First, check if the blog is associated with this location
      const { data: blogLocation, error: blogLocationError } = await supabase
        .from("blog_locations")
        .select("blog_id")
        .eq("location_id", subdomainConfig.location_id);

      if (blogLocationError) {
        console.error("Blog location error:", blogLocationError);
        throw new Error("Blog not found");
      }

      if (!blogLocation || blogLocation.length === 0) {
        console.log("No blogs associated with this location");
        throw new Error("Blog not found");
      }

      const blogIds = blogLocation.map(bl => bl.blog_id);

      // Fetch blog data
      const { data: blogData, error: blogError } = await supabase
        .from("blogs")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .in("id", blogIds)
        .single();

      console.log("Blog query result:", { blogData, blogError });

      if (blogError || !blogData) {
        console.error("Blog fetch error:", blogError);
        throw new Error("Blog not found");
      }

      setBlog(blogData);

      // Increment view count
      await supabase
        .from("blogs")
        .update({ views_count: (blogData.views_count || 0) + 1 })
        .eq("id", blogData.id);

      // Check for published posts
      const { data: postLocations } = await supabase
        .from("gmb_post_locations")
        .select("post_id, metadata")
        .eq("location_id", subdomainConfig.location_id)
        .eq("status", "published")
        .limit(6);

      if (postLocations && postLocations.length > 0) {
        const postIds = postLocations.map(p => p.post_id);
        const { data: postContent } = await supabase
          .from("gmb_post_content")
          .select("id, summary")
          .in("id", postIds);

        let postsWithContent = [];
        if (postContent && postContent.length > 0) {
          postsWithContent = postContent.filter(content =>
            content.summary && content.summary.trim().length > 0
          );
        } else {
          postsWithContent = postLocations.filter(loc => {
            const summary = loc.metadata?.summary || loc.metadata?.text || "";
            return summary && summary.trim().length > 0;
          });
        }
        setHasPublishedPosts(postsWithContent.length > 0);
      }

      // Check for reviews
      const { count: reviewCount } = await supabase
        .from("gmb_reviews")
        .select("*", { count: 'exact', head: true })
        .eq("location_id", subdomainConfig.location_id);
      setHasReviews((reviewCount || 0) > 0);

      // Check for blogs
      const { data: allBlogLocations } = await supabase
        .from("blog_locations")
        .select("blog_id")
        .eq("location_id", subdomainConfig.location_id);

      if (allBlogLocations && allBlogLocations.length > 0) {
        const allBlogIds = allBlogLocations.map(bl => bl.blog_id);
        const { count: blogCount } = await supabase
          .from("blogs")
          .select("*", { count: 'exact', head: true })
          .in("id", allBlogIds)
          .eq("status", "published");
        setHasBlogs((blogCount || 0) > 0);
      }

      // Check for products/services
      const { count: productCount } = await supabase
        .from("products_services")
        .select("*", { count: 'exact', head: true })
        .eq("workspace_id", subdomainConfig.workspace_id);
      setHasProductsServices((productCount || 0) > 0);

      // Check for gallery photos
      const hasGalleryPhotos = (locationData?.photos?.length || 0) > 0;
      setHasGallery(hasGalleryPhotos);

      // Check for social links
      if (workspaceData) {
        const hasSocialLinks = !!(
          workspaceData.facebook_url ||
          workspaceData.instagram_url ||
          workspaceData.twitter_url ||
          workspaceData.linkedin_url ||
          workspaceData.youtube_url
        );
        setHasSocial(hasSocialLinks);
      }
    } catch (err: any) {
      console.error("Error fetching blog:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd MMM yy");
    } catch (error) {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error || !blog || !subdomainData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Blog Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The blog post you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate(`${getBasePath()}/`)}>
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  const theme = subdomainData.theme_config;

  // Build theme classes from theme_config
  const buildThemeClasses = () => {
    const classes = [`theme-${theme?.theme || 'light'}`];

    if (theme?.styles) {
      Object.values(theme.styles).forEach((styleValue) => {
        if (styleValue) {
          classes.push(styleValue as string);
        }
      });
    }

    return classes.join(' ');
  };

  return (
    <>
      <Helmet>
        <title>{blog.seo_title || blog.title}</title>
        {blog.seo_description && <meta name="description" content={blog.seo_description} />}
        {blog.seo_keywords && blog.seo_keywords.length > 0 && (
          <meta name="keywords" content={blog.seo_keywords.join(", ")} />
        )}
      </Helmet>

      <div className={`subdomain-landing-root ${buildThemeClasses()} min-h-screen flex flex-col`}>
        <LandingNavbar
          businessName={subdomainData.workspace.brand_name || subdomainData.location.name}
          logoUrl={subdomainData.workspace.logo_url}
          pages={[]}
          theme={theme}
          hasPublishedPosts={hasPublishedPosts}
          hasReviews={hasReviews}
          hasBlogs={hasBlogs}
          hasProductsServices={hasProductsServices}
          hasServices={hasServices}
          hasGallery={hasGallery}
          hasSocial={hasSocial}
          hasAppointment={hasAppointment}
          phoneNumber={location?.primary_phone}
          editMode={false}
          onTrackPhoneClick={undefined}
          location={location}
          workspace={workspace}
          getCleanedServices={getCleanedServices}
        />

        <main className="flex-grow">
          {/* Back Button */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <Button
              variant="ghost"
              onClick={() => navigate(`${getBasePath()}/blogs`)}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blogs
            </Button>
          </div>

          {/* Blog Content */}
          <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Category */}
            {blog.category && (
              <div className="mb-4">
                <span
                  className="text-sm font-semibold px-4 py-2 rounded-full"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--primary) 20%, transparent)",
                    color: "var(--primary)",
                  }}
                >
                  {blog.category}
                </span>
              </div>
            )}

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{blog.title}</h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
              {blog.author_name && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{blog.author_name}</span>
                </div>
              )}
              {blog.published_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(blog.published_at)}</span>
                </div>
              )}
              {blog.read_time_minutes && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{blog.read_time_minutes} min read</span>
                </div>
              )}
            </div>

            {/* Featured Image */}
            {blog.featured_image_url && (
              <div className="mb-8 rounded-lg overflow-hidden">
                <BlogFeaturedImage
                  url={blog.featured_image_url}
                  alt={blog.title}
                  className="w-full h-auto"
                  eager
                />
              </div>
            )}

            {/* Content */}
            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(blog.content) }}
            />

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t">
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                    >
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </article>
        </main>

        <LandingFooter
          businessName={subdomainData.workspace.brand_name || subdomainData.location.name}
          businessInfo={{
            phone: subdomainData.location.name,
            address: null,
            formatted_address: null,
          }}
          theme={theme}
          workspace={subdomainData.workspace}
          location={subdomainData.location}
        />
      </div>
    </>
  );
};

export default BlogDetailPage;